import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ImportMapContext } from '../../components/ImportMapContext';
import { workspaceViewportOptions } from './workspaceViewport';

import { Icon } from '../../components/Icon';
import { MapSurface } from '../../map/MapSurface';
import {
  compositionAnnotationRenderScale,
  compositionCameraFromMapView,
  effectiveCompositionCamera,
  mapViewForCompositionCamera,
} from '../../composition/scale';
import { ColourControl } from './style/ColourControl';
import type { ApplicationMode } from '../modes/applicationModes';
import { useAppStore } from '../state/useAppStore';
import { FeaturePopup } from './popups/FeaturePopup';
import { isTableSource, isVectorSource } from '../../data/model';
import { AttributeTablePanel } from './data/AttributeTablePanel';
import { effectiveVectorSourceForLayer } from '../../joins/effectiveLayer';
import { AnchoredMenu } from './AnchoredMenu';
import { useCapability } from '../../features/useCapability';
import type { SelectionMethod } from '../../selection/model';
import { SelectByAttributeDialog } from './selection/SelectByAttributeDialog';
import { CreateAreaDialog } from './selection/CreateAreaDialog';
import { canonicalSelectedFeatures } from '../../selection/selection';
import type { VectorFeature } from '../../data/model';
import { CompositionCanvas } from './CompositionCanvas';
import { compositionPageDimensionsMm } from '../../composition/geometry';
import {
  BASEMAP_PRESETS,
  BASEMAP_SEMANTIC_GROUPS,
  basemapCustomizationFor,
  basemapPreset,
  basemapSupportsSemanticGroup,
  effectiveBasemapCustomization,
  isBasemapPresetCustomized,
  type BasemapColourGroup,
  type BasemapPresetId,
  type BasemapSemanticGroup,
  type CuratedBasemapPresetId,
} from '../../basemaps/catalog';
import { BASEMAP_PRESENTATIONS, basemapPresentationStatus } from '../../basemaps/presentation';
import {
  BASEMAP_LABEL_PRESENTATIONS,
  basemapLabelPresentationStatus,
  isBasemapLabelPresentationId,
} from '../../basemaps/labelPresentation';
import { clampedHorizontalPopoverOffset } from './anchoredMenuPosition';
import {
  DEFAULT_LABEL_APPEARANCE,
  BASEMAP_LABEL_HALOS,
  type BasemapLabelAppearance,
} from '../../basemaps/labelAppearance';
import { MapSearch } from './MapSearch';
import { ROAD_LABEL_TIERS, effectiveRoadLabelTiers } from '../../basemaps/roadHierarchy';
import { ROAD_LABEL_DENSITIES } from '../../basemaps/labelAppearance';

interface MapWorkspaceProps {
  readonly mode: Exclude<ApplicationMode, 'empty'>;
}

function MapToolsRail() {
  const active = useAppStore((state) => state.shell.selectionToolActive);
  const method = useAppStore((state) => state.shell.selectionMethod);
  const setSelectionToolActive = useAppStore((state) => state.setSelectionToolActive);
  const setSelectionMethod = useAppStore((state) => state.setSelectionMethod);
  const canSelectByAttribute = useCapability('selection.byAttribute');
  const querySchemaSignature = useAppStore((state) =>
    state.project.layers
      .map((layer) =>
        [
          layer.id,
          ...layer.joins.flatMap((join) => [
            join.id,
            ...join.fields.map((field) => field.outputFieldId),
          ]),
        ].join(':'),
      )
      .join('|'),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [queryOpen, setQueryOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!active) return;
    const exitSelection = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) setSelectionToolActive(false);
    };
    window.addEventListener('keydown', exitSelection);
    return () => window.removeEventListener('keydown', exitSelection);
  }, [active, setSelectionToolActive]);

  const chooseMethod = (next: SelectionMethod) => {
    setSelectionMethod(next);
    setMenuOpen(false);
  };

  return (
    <>
      <div className="map-tools-rail" aria-label="Map tools">
        <div className="selection-tool-control">
          <button
            aria-label={method === 'lasso' ? 'Lasso select' : 'Select features'}
            aria-pressed={active}
            className={active ? 'icon-button map-tool-active' : 'icon-button'}
            onClick={() => setSelectionToolActive(!active)}
            title={
              active
                ? method === 'lasso'
                  ? 'Lasso select: drag to replace, Shift to add, Alt/Option to subtract, Ctrl/Cmd to toggle. Escape cancels drawing or exits.'
                  : `Exit ${method === 'rectangle' ? 'Rectangle' : 'Click'} selection (Escape)`
                : 'Select features'
            }
            type="button"
          >
            <Icon name={method === 'lasso' ? 'select-lasso' : 'select'} />
          </button>
          <button
            aria-expanded={menuOpen}
            aria-haspopup="menu"
            aria-label="Choose selection type"
            className={
              active ? 'selection-method-button map-tool-active' : 'selection-method-button'
            }
            onClick={() => setMenuOpen((open) => !open)}
            ref={menuButtonRef}
            title={`Selection method: ${method === 'lasso' ? 'Lasso' : method === 'rectangle' ? 'Rectangle' : 'Click'}`}
            type="button"
          >
            <Icon name="chevron-down" />
          </button>
          <AnchoredMenu
            anchorRef={menuButtonRef}
            className="selection-method-menu"
            label="Selection methods"
            onDismiss={() => setMenuOpen(false)}
            open={menuOpen}
          >
            <button
              aria-current={method === 'click' ? 'true' : undefined}
              onClick={() => chooseMethod('click')}
              role="menuitem"
              type="button"
            >
              <span className="selection-menu-label">
                <Icon name="select" />
                <span>Click</span>
              </span>
              {method === 'click' ? <Icon name="check" /> : null}
            </button>
            <button
              aria-current={method === 'rectangle' ? 'true' : undefined}
              onClick={() => chooseMethod('rectangle')}
              role="menuitem"
              type="button"
            >
              <span className="selection-menu-label">
                <Icon name="select-rectangle" />
                <span>Rectangle</span>
              </span>
              {method === 'rectangle' ? <Icon name="check" /> : null}
            </button>
            <button
              aria-current={method === 'lasso' ? 'true' : undefined}
              onClick={() => chooseMethod('lasso')}
              role="menuitem"
              title="Drag to replace; Shift adds; Alt/Option subtracts; Ctrl/Cmd toggles"
              type="button"
            >
              <span className="selection-menu-label">
                <Icon name="select-lasso" />
                <span>Lasso select</span>
              </span>
              {method === 'lasso' ? <Icon name="check" /> : null}
            </button>
            <button
              aria-label={
                canSelectByAttribute ? 'Select by attribute' : 'Select by attribute, Pro required'
              }
              disabled={!canSelectByAttribute}
              onClick={() => {
                setMenuOpen(false);
                setQueryOpen(true);
              }}
              role="menuitem"
              title={canSelectByAttribute ? 'Select by attribute' : 'Requires Placeio Maps Pro'}
              type="button"
            >
              <span className="selection-menu-label">
                <Icon name="select-attribute" />
                <span>Select by attribute…</span>
              </span>
              {!canSelectByAttribute ? <span className="pro-tag">Pro</span> : null}
            </button>
          </AnchoredMenu>
        </div>
      </div>
      {queryOpen ? (
        <SelectByAttributeDialog key={querySchemaSignature} onClose={() => setQueryOpen(false)} />
      ) : null}
    </>
  );
}

export function SelectedFeaturesBar({
  count,
  onClear,
  onCreateArea,
  canCreateArea = false,
}: {
  readonly count: number;
  readonly onClear: () => void;
  readonly onCreateArea?: (() => void) | undefined;
  readonly canCreateArea?: boolean;
}) {
  if (count <= 0) return null;
  return (
    <div className="selected-features-bar">
      <strong role="status" aria-live="polite">
        {count.toLocaleString()} {count === 1 ? 'feature' : 'features'} selected
      </strong>
      {onCreateArea ? (
        <button
          type="button"
          onClick={onCreateArea}
          disabled={!canCreateArea}
          title={canCreateArea ? 'Create area from selection' : 'Annotation authoring requires Pro'}
        >
          Create area…{!canCreateArea ? ' (Pro)' : ''}
        </button>
      ) : null}
      <button aria-label="Clear selection" onClick={onClear} type="button">
        Clear
      </button>
    </div>
  );
}

function MapUtilityTray({
  canAuthorBasemap,
  composition = false,
}: {
  readonly canAuthorBasemap: boolean;
  readonly composition?: boolean;
}) {
  const [activePanel, setActivePanel] = useState<'search' | 'basemap' | 'view' | null>(null);
  const isBasemapOpen = activePanel === 'basemap';
  const [isBasemapAppearanceOpen, setIsBasemapAppearanceOpen] = useState(false);
  const isViewOpen = activePanel === 'view';
  const trayRef = useRef<HTMLDivElement | null>(null);
  const basemapButtonRef = useRef<HTMLButtonElement | null>(null);
  const basemapAppearanceButtonRef = useRef<HTMLButtonElement | null>(null);
  const viewButtonRef = useRef<HTMLButtonElement | null>(null);
  const viewAnchorRef = useRef<HTMLDivElement | null>(null);
  const viewPopoverRef = useRef<HTMLDivElement | null>(null);
  const fitToData = useAppStore((state) => state.fitToData);
  const editorView = useAppStore((state) => state.project.map.view);
  const compositionCamera = useAppStore((state) => state.project.composition.mapFrame.camera);
  const mapView = composition ? compositionCamera : editorView;
  const backgroundColour = useAppStore((state) => state.project.map.backgroundColour);
  const basemap = useAppStore((state) => state.project.map.basemap);
  const setBasemapPreset = useAppStore((state) => state.setBasemapPreset);
  const applyBasemapPresentation = useAppStore((state) => state.applyBasemapPresentation);
  const applyLabelPresentation = useAppStore((state) => state.applyBasemapLabelPresentation);
  const setBasemapBuildings3d = useAppStore((state) => state.setBasemapBuildings3d);
  const setBasemapContextOpacity = useAppStore((state) => state.setBasemapContextOpacity);
  const setBasemapLabelsOpacity = useAppStore((state) => state.setBasemapLabelsOpacity);
  const setLabelAppearance = useAppStore((state) => state.setBasemapLabelAppearance);
  const resetLabels = useAppStore((state) => state.resetBasemapLabelAppearance);
  const setBasemapVisibility = useAppStore((state) => state.setBasemapVisibility);
  const setBasemapColour = useAppStore((state) => state.setBasemapColour);
  const resetBasemapCustomization = useAppStore((state) => state.resetBasemapCustomization);
  const setMapBackgroundColour = useAppStore((state) => state.setMapBackgroundColour);
  const updateMapView = useAppStore((state) => state.updateMapView);
  const updateCompositionCamera = useAppStore((state) => state.updateCompositionCamera);
  const changeView = (patch: { readonly pitch?: number; readonly bearing?: number }) => {
    if (composition) updateCompositionCamera({ ...compositionCamera, ...patch });
    else updateMapView({ ...editorView, ...patch });
  };
  const canAuthor3dBuildings = useCapability('basemap.buildings3d');
  const canCustomizeBasemap = useCapability('basemap.customize');
  const canPitch = useCapability('map.pitch');

  useEffect(() => {
    if (!isBasemapOpen && !isViewOpen) return;
    const dismiss = (event: MouseEvent) => {
      if (!trayRef.current?.contains(event.target as Node)) {
        setActivePanel(null);
        setIsBasemapAppearanceOpen(false);
      }
    };
    const dismissOnFocusOutside = (event: FocusEvent) => {
      if (!trayRef.current?.contains(event.target as Node)) {
        setActivePanel(null);
        setIsBasemapAppearanceOpen(false);
      }
    };
    document.addEventListener('mousedown', dismiss);
    document.addEventListener('focusin', dismissOnFocusOutside);
    return () => {
      document.removeEventListener('mousedown', dismiss);
      document.removeEventListener('focusin', dismissOnFocusOutside);
    };
  }, [isBasemapOpen, isViewOpen]);

  useEffect(() => {
    if (!isBasemapOpen && !isViewOpen) return;
    const dismissWithEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (isBasemapAppearanceOpen) {
        setIsBasemapAppearanceOpen(false);
        requestAnimationFrame(() => basemapAppearanceButtonRef.current?.focus());
      } else if (isBasemapOpen) {
        setActivePanel(null);
        requestAnimationFrame(() => basemapButtonRef.current?.focus());
      } else {
        setActivePanel(null);
        requestAnimationFrame(() => viewButtonRef.current?.focus());
      }
    };
    document.addEventListener('keydown', dismissWithEscape, true);
    return () => document.removeEventListener('keydown', dismissWithEscape, true);
  }, [isBasemapAppearanceOpen, isBasemapOpen, isViewOpen]);

  useLayoutEffect(() => {
    if (!isViewOpen && !isBasemapOpen) return;
    const updatePosition = () => {
      const viewport = trayRef.current?.closest<HTMLElement>('.map-viewport');
      if (!viewport) return;
      const bounds = viewport.getBoundingClientRect();
      const trayBounds = trayRef.current!.getBoundingClientRect();
      const popovers = trayRef.current?.querySelectorAll<HTMLElement>('.menu-popover');
      popovers?.forEach((popover) => {
        const anchor = popover.parentElement;
        if (!anchor) return;
        popover.style.maxWidth = `${Math.max(0, bounds.width - 24)}px`;
        const advanced = popover.classList.contains('basemap-appearance-popover');
        const main = popover.classList.contains('basemap-popover');
        const anchorBounds = anchor.getBoundingClientRect();
        if (advanced) {
          popover.dataset.columns = bounds.width >= 960 && bounds.height >= 600 ? 'two' : 'one';
          popover.style.maxHeight = `${Math.max(0, bounds.height - 64)}px`;
        }
        if (composition) popover.style.maxHeight = `${Math.max(0, bounds.height - 64)}px`;
        // Basemap rises from the entire tray; its companion shares that panel's bottom edge.
        if (main) {
          popover.style.bottom = `calc(${anchorBounds.bottom - trayBounds.top}px + 0.45rem)`;
          popover.style.maxHeight = `${Math.max(0, trayBounds.top - bounds.top - 16)}px`;
        }
        const primary = trayRef.current!.querySelector<HTMLElement>('.basemap-popover');
        const primaryBounds = primary?.getBoundingClientRect();
        const gap = primaryBounds ? trayBounds.top - primaryBounds.bottom : 0;
        const preferredLeft =
          advanced && primaryBounds
            ? primaryBounds.right + gap - anchorBounds.left
            : main
              ? trayBounds.left - anchorBounds.left
              : 0;
        popover.style.left = `${preferredLeft}px`;
        if (advanced && primaryBounds) {
          popover.style.bottom = `${anchorBounds.bottom - primaryBounds.bottom}px`;
          popover.style.maxHeight = `${Math.max(0, primaryBounds.bottom - bounds.top - 8)}px`;
          popover.style.height =
            popover.dataset.columns === 'two' ? `${primaryBounds.height}px` : '';
        }
        const offset = clampedHorizontalPopoverOffset(
          { left: anchorBounds.left + preferredLeft },
          popover.getBoundingClientRect(),
          bounds,
        );
        popover.style.left = `${preferredLeft + offset}px`;
        popover.style.visibility = 'visible';
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [isViewOpen, isBasemapOpen, isBasemapAppearanceOpen, composition, basemap.presetId]);

  const chooseBasemap = (presetId: BasemapPresetId) => setBasemapPreset(presetId);
  const activePresetId =
    basemap.presetId === 'none' ? null : (basemap.presetId as CuratedBasemapPresetId);
  const activeCustomization = activePresetId
    ? basemapCustomizationFor(basemap, activePresetId)
    : null;
  const effectiveCustomization = activePresetId
    ? effectiveBasemapCustomization(basemap, activePresetId)
    : null;
  const presentationStatus = basemapPresentationStatus(basemap);
  const labelStatus = basemapLabelPresentationStatus(basemap);

  return (
    <div className="map-utility-tray" aria-label="Map utilities" ref={trayRef}>
      {!composition ? (
        <MapSearch
          open={activePanel === 'search'}
          onDismiss={() => setActivePanel(null)}
          onToggle={() => {
            setIsBasemapAppearanceOpen(false);
            setActivePanel((panel) => (panel === 'search' ? null : 'search'));
          }}
        />
      ) : null}
      {canAuthorBasemap ? (
        <div className="basemap-control">
          <button
            aria-controls="basemap-popover"
            aria-expanded={isBasemapOpen}
            className="utility-button"
            onClick={() => {
              setIsBasemapAppearanceOpen(false);
              setActivePanel((panel) => (panel === 'basemap' ? null : 'basemap'));
            }}
            ref={basemapButtonRef}
            title="Basemap"
            type="button"
          >
            <Icon name="map" />
            <span>Basemap</span>
          </button>
          {isBasemapOpen ? (
            <div
              aria-label="Basemap"
              className="menu-popover basemap-popover"
              id="basemap-popover"
              role="group"
            >
              <section className="basemap-section" aria-label="Basemap family">
                <strong className="basemap-section-heading">Basemap</strong>
                <div aria-label="Basemap presets" className="basemap-preset-list" role="radiogroup">
                  {BASEMAP_PRESETS.map((preset, index) => {
                    const curatedPresetId =
                      preset.id === 'none' ? null : (preset.id as CuratedBasemapPresetId);
                    const modified = curatedPresetId
                      ? isBasemapPresetCustomized(basemap, curatedPresetId)
                      : false;
                    return (
                      <div className="basemap-preset-row" key={preset.id}>
                        <button
                          aria-checked={basemap.presetId === preset.id}
                          className={
                            basemap.presetId === preset.id
                              ? 'basemap-preset-option basemap-preset-option-selected'
                              : 'basemap-preset-option'
                          }
                          id={`basemap-preset-${preset.id}`}
                          onClick={() => chooseBasemap(preset.id)}
                          onKeyDown={(event) => {
                            if (
                              !['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(
                                event.key,
                              )
                            )
                              return;
                            event.preventDefault();
                            const delta =
                              event.key === 'ArrowUp' || event.key === 'ArrowLeft' ? -1 : 1;
                            const next =
                              BASEMAP_PRESETS[
                                (index + delta + BASEMAP_PRESETS.length) % BASEMAP_PRESETS.length
                              ];
                            if (!next) return;
                            chooseBasemap(next.id);
                            requestAnimationFrame(() =>
                              document.getElementById(`basemap-preset-${next.id}`)?.focus(),
                            );
                          }}
                          role="radio"
                          tabIndex={basemap.presetId === preset.id ? 0 : -1}
                          type="button"
                        >
                          <BasemapPreview presetId={preset.id} />
                          <span className="basemap-preset-copy">
                            <span>{preset.label}</span>
                            <small>
                              {preset.description}
                              {modified ? <span className="basemap-modified">Modified</span> : null}
                            </small>
                          </span>
                          {basemap.presetId === preset.id ? <Icon name="check" /> : null}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </section>
              {activePresetId ? (
                <section
                  className="basemap-section basemap-presentation"
                  aria-label="Basemap presentation"
                >
                  <div className="basemap-section-heading">
                    <strong>Presentation</strong>
                    {presentationStatus === 'custom' ? (
                      <span role="status" aria-label="Current basemap presentation">
                        Custom
                      </span>
                    ) : null}
                  </div>
                  <div
                    className="basemap-presentation-options"
                    role="group"
                    aria-label="Presentation choices"
                  >
                    {BASEMAP_PRESENTATIONS.map(({ id, label, description }) => (
                      <button
                        aria-pressed={presentationStatus === id}
                        key={id}
                        onClick={() => applyBasemapPresentation(id)}
                        title={description}
                        type="button"
                      >
                        <span>{label}</span>
                        {presentationStatus === id ? <Icon name="check" /> : null}
                      </button>
                    ))}
                  </div>
                </section>
              ) : null}
              {activePresetId ? (
                <section
                  className="basemap-section basemap-label-presentation"
                  aria-label="Basemap labels"
                >
                  <div className="basemap-section-heading">
                    <label htmlFor="basemap-label-presentation">Labels</label>
                  </div>
                  <select
                    id="basemap-label-presentation"
                    aria-label="Basemap label presentation"
                    value={basemap.labelPresentation ?? 'follow-basemap'}
                    onChange={(event) => {
                      if (isBasemapLabelPresentationId(event.target.value))
                        applyLabelPresentation(event.target.value);
                    }}
                  >
                    {BASEMAP_LABEL_PRESENTATIONS.map(({ id, label }) => (
                      <option key={id} value={id}>
                        {label}
                      </option>
                    ))}
                  </select>
                </section>
              ) : null}
              <section className="basemap-section basemap-map-options" aria-label="Map options">
                <strong className="basemap-section-heading">Map options</strong>
                <ColourControl
                  id="map-background-colour"
                  label="Background colour"
                  onChange={(colour) => setMapBackgroundColour(colour)}
                  value={backgroundColour}
                />
                {canAuthor3dBuildings && basemap.presetId !== 'none' ? (
                  <div className="basemap-3d-control">
                    <label htmlFor="basemap-buildings-3d">3D buildings</label>
                    <input
                      checked={basemap.buildings3d}
                      id="basemap-buildings-3d"
                      onChange={(event) => setBasemapBuildings3d(event.target.checked)}
                      type="checkbox"
                    />
                  </div>
                ) : null}
              </section>
              {canCustomizeBasemap && basemap.presetId !== 'none' ? (
                <section className="basemap-section" aria-label="Basemap customisation">
                  <strong className="basemap-section-heading">Advanced</strong>
                  <button
                    aria-controls="basemap-customisation-popover"
                    aria-expanded={isBasemapAppearanceOpen}
                    className="basemap-appearance-action"
                    onClick={() => setIsBasemapAppearanceOpen((open) => !open)}
                    ref={basemapAppearanceButtonRef}
                    type="button"
                  >
                    <span>Customise basemap</span>
                    <Icon name="chevron-right" />
                  </button>
                </section>
              ) : null}
            </div>
          ) : null}
          {isBasemapOpen &&
          isBasemapAppearanceOpen &&
          activePresetId &&
          activeCustomization &&
          effectiveCustomization ? (
            <BasemapCustomizationPanel
              colourOverrides={activeCustomization.colours}
              colours={effectiveCustomization.colours}
              contextOpacity={effectiveCustomization.contextOpacity}
              labelsOpacity={effectiveCustomization.labelsOpacity}
              labelAppearance={activeCustomization.labelAppearance ?? DEFAULT_LABEL_APPEARANCE}
              labelsCustom={labelStatus === 'custom'}
              onLabelAppearanceChange={setLabelAppearance}
              onResetLabels={resetLabels}
              onColourChange={setBasemapColour}
              onContextOpacityChange={setBasemapContextOpacity}
              onLabelsOpacityChange={setBasemapLabelsOpacity}
              onReset={resetBasemapCustomization}
              onVisibilityChange={setBasemapVisibility}
              presetLabel={basemapPreset(activePresetId).label}
              supportedGroups={BASEMAP_SEMANTIC_GROUPS.filter((group) =>
                basemapSupportsSemanticGroup(activePresetId, group),
              )}
              visibility={effectiveCustomization.visibility}
            />
          ) : null}
        </div>
      ) : null}
      <div className="view-control" ref={viewAnchorRef}>
        <button
          aria-controls="view-popover"
          aria-expanded={isViewOpen}
          className="utility-button"
          onClick={() => {
            setIsBasemapAppearanceOpen(false);
            setActivePanel((panel) => (panel === 'view' ? null : 'view'));
          }}
          ref={viewButtonRef}
          title="View"
          type="button"
        >
          <Icon name="eye" />
          <span>View</span>
        </button>
        {isViewOpen ? (
          <div
            aria-label="View"
            className="menu-popover view-popover"
            id="view-popover"
            ref={viewPopoverRef}
            role="group"
            style={{ visibility: 'hidden' }}
          >
            <strong>View</strong>
            {canPitch ? (
              <>
                <label htmlFor="map-view-pitch">
                  <span>Pitch</span>
                  <output>{Math.round(mapView.pitch)}°</output>
                </label>
                <input
                  aria-label="Pitch"
                  aria-valuetext={`${Math.round(mapView.pitch)} degrees`}
                  id="map-view-pitch"
                  max="60"
                  min="0"
                  onChange={(event) => changeView({ pitch: Number(event.target.value) })}
                  step="1"
                  type="range"
                  value={mapView.pitch}
                />
              </>
            ) : null}
            <label htmlFor="map-view-bearing">
              <span>Bearing</span>
              <output>{Math.round(mapView.bearing)}°</output>
            </label>
            <input
              aria-label="Bearing"
              aria-valuetext={`${Math.round(mapView.bearing)} degrees`}
              id="map-view-bearing"
              max="180"
              min="-180"
              onChange={(event) => changeView({ bearing: Number(event.target.value) })}
              step="1"
              type="range"
              value={mapView.bearing}
            />
            <div className="view-popover-actions">
              {canPitch ? (
                <button onClick={() => changeView({ pitch: 0 })} type="button">
                  Top down
                </button>
              ) : null}
              <button onClick={() => changeView({ bearing: 0 })} type="button">
                Reset north
              </button>
            </div>
          </div>
        ) : null}
      </div>
      {!composition ? (
        <button
          className="utility-button"
          onClick={(event) => fitToData(workspaceViewportOptions(event.currentTarget))}
          title="Fit to data"
          type="button"
        >
          <Icon name="fit" />
          <span>Fit</span>
        </button>
      ) : null}
    </div>
  );
}

const BASEMAP_LABEL_SECTIONS: readonly {
  readonly label: string;
  readonly controls: readonly { readonly group: BasemapSemanticGroup; readonly label: string }[];
}[] = [
  {
    label: 'Places',
    controls: [
      { group: 'citiesTowns', label: 'Cities & towns' },
      { group: 'suburbsLocalities', label: 'Suburbs & localities' },
    ],
  },
  {
    label: 'Transport',
    controls: [
      { group: 'roadNames', label: 'Road names' },
      { group: 'routeShields', label: 'Route shields' },
      { group: 'transitLabels', label: 'Transit labels' },
    ],
  },
  {
    label: 'Other',
    controls: [
      { group: 'pointsOfInterest', label: 'Points of interest' },
      { group: 'naturalWaterLabels', label: 'Natural & water' },
      { group: 'otherLabels', label: 'Other labels' },
    ],
  },
];

const BASEMAP_FEATURE_COLUMNS: readonly (readonly {
  readonly group: BasemapSemanticGroup;
  readonly label: string;
}[])[] = [
  [
    { group: 'roads', label: 'Roads' },
    { group: 'transitLines', label: 'Transit lines' },
    { group: 'naturalVegetation', label: 'Natural vegetation' },
    { group: 'parksGreenSpace', label: 'Parks & green space' },
    { group: 'buildings', label: 'Buildings' },
  ],
  [
    { group: 'water', label: 'Water' },
    { group: 'countryBoundaries', label: 'Country boundaries' },
    { group: 'regionalBoundaries', label: 'Regional boundaries' },
    { group: 'localBoundaries', label: 'Local boundaries' },
  ],
];

const BASEMAP_COLOUR_CONTROLS: readonly {
  readonly group: BasemapColourGroup;
  readonly label: string;
}[] = [
  { group: 'water', label: 'Water' },
  { group: 'motorwaysHighways', label: 'Motorways & highways' },
  { group: 'arterialRoads', label: 'Arterial roads' },
  { group: 'collectorRoads', label: 'Collector roads' },
  { group: 'localRoads', label: 'Local roads' },
  { group: 'parksGreenSpace', label: 'Parks & green space' },
  { group: 'naturalVegetation', label: 'Natural vegetation' },
];

function BasemapCustomizationPanel({
  colourOverrides,
  colours,
  contextOpacity,
  labelsOpacity,
  labelAppearance,
  labelsCustom,
  onLabelAppearanceChange,
  onResetLabels,
  onColourChange,
  onContextOpacityChange,
  onLabelsOpacityChange,
  onReset,
  onVisibilityChange,
  presetLabel,
  supportedGroups,
  visibility,
}: {
  readonly colourOverrides: Readonly<Record<BasemapColourGroup, string | null>>;
  readonly colours: Readonly<Record<BasemapColourGroup, string>>;
  readonly contextOpacity: number;
  readonly labelsOpacity: number;
  readonly labelAppearance: BasemapLabelAppearance;
  readonly labelsCustom: boolean;
  readonly onLabelAppearanceChange: (patch: Partial<BasemapLabelAppearance>) => unknown;
  readonly onResetLabels: () => unknown;
  readonly onColourChange: (group: BasemapColourGroup, colour: string | null) => unknown;
  readonly onContextOpacityChange: (opacity: number) => unknown;
  readonly onLabelsOpacityChange: (opacity: number) => unknown;
  readonly onReset: () => unknown;
  readonly onVisibilityChange: (group: BasemapSemanticGroup, visible: boolean) => unknown;
  readonly presetLabel: string;
  readonly supportedGroups: readonly BasemapSemanticGroup[];
  readonly visibility: Readonly<Record<BasemapSemanticGroup, boolean>>;
}) {
  const contextPercent = Math.round(contextOpacity * 100);
  const labelsPercent = Math.round(labelsOpacity * 100);
  const roadTiers = effectiveRoadLabelTiers(labelAppearance.roadTiers, visibility.roadNames);
  const haloIndex = BASEMAP_LABEL_HALOS.indexOf(labelAppearance.halo);
  const densityIndex = ROAD_LABEL_DENSITIES.indexOf(labelAppearance.roadDensity ?? 'normal');
  const haloLabel = labelAppearance.halo[0]!.toUpperCase() + labelAppearance.halo.slice(1);
  const densityLabel = ROAD_LABEL_DENSITIES[densityIndex]!;
  const densityText = densityLabel[0]!.toUpperCase() + densityLabel.slice(1);
  return (
    <div
      aria-label={`${presetLabel} customisation`}
      className="menu-popover basemap-appearance-popover"
      id="basemap-customisation-popover"
      role="group"
    >
      <strong>{presetLabel} customisation</strong>
      <div className="basemap-customisation-columns">
        <div className="basemap-customisation-column">
          <section>
            <h3>Opacity</h3>
            <label htmlFor="basemap-context-opacity">
              <span>Basemap opacity</span>
              <output>{contextPercent}%</output>
            </label>
            <input
              aria-label="Basemap opacity"
              aria-valuetext={`${contextPercent}%`}
              id="basemap-context-opacity"
              max="100"
              min="0"
              onChange={(event) => onContextOpacityChange(Number(event.target.value) / 100)}
              step="5"
              type="range"
              value={contextPercent}
            />
            <label htmlFor="basemap-labels-opacity">
              <span>Basemap labels opacity</span>
              <output>{labelsPercent}%</output>
            </label>
            <input
              aria-label="Basemap labels opacity"
              aria-valuetext={`${labelsPercent}%`}
              id="basemap-labels-opacity"
              max="100"
              min="0"
              onChange={(event) => onLabelsOpacityChange(Number(event.target.value) / 100)}
              step="5"
              type="range"
              value={labelsPercent}
            />
          </section>
          <section>
            <h3>Label content</h3>
            <div className="basemap-label-sections">
              {BASEMAP_LABEL_SECTIONS.map(({ label: sectionLabel, controls }) => {
                const supported = controls.filter(({ group }) => supportedGroups.includes(group));
                return supported.length > 0 ? (
                  <div
                    className={`basemap-label-section${sectionLabel === 'Transport' ? ' basemap-transport-labels' : ''}`}
                    key={sectionLabel}
                  >
                    <h4>{sectionLabel}</h4>
                    <div className="basemap-feature-grid">
                      {supported.flatMap(({ group, label }) =>
                        group === 'roadNames'
                          ? ROAD_LABEL_TIERS.map(([tier, tierLabel]) => (
                              <label key={tier}>
                                <input
                                  type="checkbox"
                                  aria-label={`Road names: ${tierLabel}`}
                                  checked={roadTiers[tier]}
                                  onChange={(event) =>
                                    onLabelAppearanceChange({
                                      roadTiers: { ...roadTiers, [tier]: event.target.checked },
                                    })
                                  }
                                />
                                <span>{tierLabel}</span>
                              </label>
                            ))
                          : [
                              <label key={group}>
                                <input
                                  checked={visibility[group]}
                                  onChange={(event) =>
                                    onVisibilityChange(group, event.target.checked)
                                  }
                                  type="checkbox"
                                />
                                <span>{label}</span>
                              </label>,
                            ],
                      )}
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          </section>
          <section className="basemap-label-appearance">
            <h3>
              Label appearance{' '}
              {labelsCustom ? (
                <span role="status" aria-label="Current label presentation">
                  Custom
                </span>
              ) : null}
            </h3>
            <label htmlFor="basemap-label-size">
              <span>Label size</span>
              <output>{Math.round(labelAppearance.size * 100)}%</output>
            </label>
            <input
              id="basemap-label-size"
              aria-label="Label size"
              aria-valuetext={`${Math.round(labelAppearance.size * 100)}%`}
              type="range"
              min="50"
              max="150"
              step="5"
              value={Math.round(labelAppearance.size * 100)}
              onChange={(event) =>
                onLabelAppearanceChange({ size: Number(event.target.value) / 100 })
              }
            />
            <label htmlFor="basemap-label-emphasis">
              <span>Label emphasis</span>
              <output>
                {labelAppearance.emphasis < 0
                  ? 'Subtle'
                  : labelAppearance.emphasis > 1
                    ? 'Maximum'
                    : labelAppearance.emphasis > 0
                      ? 'Strong'
                      : 'Standard'}
              </output>
            </label>
            <input
              id="basemap-label-emphasis"
              aria-label="Label emphasis"
              aria-valuetext={
                labelAppearance.emphasis < 0
                  ? 'Subtle'
                  : labelAppearance.emphasis > 1
                    ? 'Maximum'
                    : labelAppearance.emphasis > 0
                      ? 'Strong'
                      : 'Standard'
              }
              type="range"
              min="-100"
              max="200"
              step="25"
              value={labelAppearance.emphasis * 100}
              onChange={(event) =>
                onLabelAppearanceChange({ emphasis: Number(event.target.value) / 100 })
              }
            />
            <label htmlFor="basemap-label-halo">
              <span>Label halo</span>
              <output>{haloLabel}</output>
            </label>
            <input
              id="basemap-label-halo"
              aria-label="Label halo"
              aria-valuetext={haloLabel}
              type="range"
              min="0"
              max={BASEMAP_LABEL_HALOS.length - 1}
              step="1"
              value={haloIndex}
              onChange={(event) => {
                const halo = BASEMAP_LABEL_HALOS[Number(event.target.value)];
                if (halo) onLabelAppearanceChange({ halo });
              }}
            />
            {supportedGroups.includes('roadNames') ? (
              <>
                <label htmlFor="basemap-road-density">
                  <span>Road-name density</span>
                  <output>{densityText}</output>
                </label>
                <input
                  id="basemap-road-density"
                  aria-label="Road-name density"
                  aria-valuetext={densityText}
                  type="range"
                  min="0"
                  max={ROAD_LABEL_DENSITIES.length - 1}
                  step="1"
                  value={densityIndex}
                  onChange={(event) => {
                    const roadDensity = ROAD_LABEL_DENSITIES[Number(event.target.value)];
                    if (roadDensity) onLabelAppearanceChange({ roadDensity });
                  }}
                />
              </>
            ) : null}
            <button className="basemap-reset-action" type="button" onClick={() => onResetLabels()}>
              Reset label appearance
            </button>
          </section>
        </div>
        <div className="basemap-customisation-column">
          <section>
            <h3>Features</h3>
            <div className="basemap-feature-grid">
              {BASEMAP_FEATURE_COLUMNS.map((column, index) => (
                <div className="basemap-feature-column" key={index}>
                  {column
                    .filter(({ group }) => supportedGroups.includes(group))
                    .map(({ group, label }) => (
                      <label key={group}>
                        <input
                          aria-label={label}
                          checked={visibility[group]}
                          onChange={(event) => onVisibilityChange(group, event.target.checked)}
                          type="checkbox"
                        />
                        <span>{label}</span>
                      </label>
                    ))}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h3>Colours</h3>
            <div className="basemap-colour-grid">
              {BASEMAP_COLOUR_CONTROLS.map(({ group, label }) => (
                <div className="basemap-colour-override" key={group}>
                  <ColourControl
                    id={`basemap-${group}-colour`}
                    label={label}
                    onChange={(colour) => onColourChange(group, colour)}
                    value={colours[group]}
                  />
                  <button
                    disabled={colourOverrides[group] === null}
                    onClick={() => onColourChange(group, null)}
                    type="button"
                  >
                    Use default
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
      <button className="basemap-reset-action" onClick={() => onReset()} type="button">
        Reset basemap to Standard
      </button>
    </div>
  );
}

function BasemapPreview({ presetId }: { readonly presetId: BasemapPresetId }) {
  return (
    <svg
      aria-hidden="true"
      className={`basemap-preview basemap-preview-${presetId}`}
      focusable="false"
      viewBox="0 0 56 38"
    >
      <rect className="preview-land" height="38" width="56" />
      <path className="preview-water" d="M38 0h18v38H45c-5-5-4-10-8-15-5-7-4-15 1-23Z" />
      <path className="preview-minor-road" d="M-2 29 15 20l13 2 18-15" />
      <path className="preview-road" d="M7 40 17 25 22 14 35-2" />
      <path className="preview-road preview-road-cross" d="M-3 10 14 13l20 8 25 3" />
      <path className="preview-grid" d="M14 0v38M28 0v38M42 0v38M0 12h56M0 25h56" />
      <circle className="preview-place" cx="17" cy="12" r="1.5" />
      <path className="preview-label" d="M20 10h10M20 13h7M32 28h8" />
    </svg>
  );
}

export function MapWorkspace({ mode }: MapWorkspaceProps) {
  const compositionFrame = useAppStore((state) => state.project.composition.mapFrame.bounds);
  const compositionPage = useAppStore((state) => state.project.composition.page);
  const { interaction: importInteraction } = useContext(ImportMapContext);
  const [areaFeatures, setAreaFeatures] = useState<readonly VectorFeature[] | null>(null);
  const mapView = useAppStore((state) => state.project.map.view);
  const searchTarget = useAppStore((state) => state.shell.searchTarget);
  const compositionCamera = useAppStore((state) => state.project.composition.mapFrame.camera);
  const backgroundColour = useAppStore((state) => state.project.map.backgroundColour);
  const basemap = useAppStore((state) => state.project.map.basemap);
  const sources = useAppStore((state) => state.project.sources);
  const symbolAssets = useAppStore((state) => state.project.symbolAssets);
  const layers = useAppStore((state) => state.project.layers);
  const annotationLayers = useAppStore((state) => state.project.annotationLayers);
  const activeFocusAreaId = useAppStore((state) => state.project.activeFocusAreaId);
  const annotationCreationTool = useAppStore((state) => state.shell.annotationCreationTool);
  const activeAnnotationLayerId = useAppStore((state) => state.shell.activeAnnotationLayerId);
  const annotationDraftCoordinates = useAppStore((state) => state.shell.annotationDraftCoordinates);
  const selectedAnnotationId = useAppStore((state) => state.shell.selectedAnnotationId);
  const inspectedFeature = useAppStore((state) => state.shell.inspectedFeature);
  const selectionToolActive = useAppStore((state) => state.shell.selectionToolActive);
  const selectionMethod = useAppStore((state) => state.shell.selectionMethod);
  const selectedFeatures = useAppStore((state) => state.shell.selectedFeatures);
  const selectedLayerId = useAppStore((state) => state.shell.selectedLayerId);
  const selectedTableSourceId = useAppStore((state) => state.shell.selectedTableSourceId);
  const attributeTableOpen = useAppStore((state) => state.shell.attributeTableOpen);
  const closeAttributeTable = useAppStore((state) => state.closeAttributeTable);
  const updateMapView = useAppStore((state) => state.updateMapView);
  const updateCompositionCamera = useAppStore((state) => state.updateCompositionCamera);
  const canPitch = useCapability('map.pitch');
  const canRender3dBuildings = useCapability('basemap.buildings3d');
  const inspectFeature = useAppStore((state) => state.inspectFeature);
  const selectFeatureFromMap = useAppStore((state) => state.selectFeatureFromMap);
  const selectFeaturesFromMap = useAppStore((state) => state.selectFeaturesFromMap);
  const clearSelection = useAppStore((state) => state.clearSelection);
  const placeAnnotationCoordinate = useAppStore((state) => state.placeAnnotationCoordinate);
  const finishAnnotationDrawing = useAppStore((state) => state.finishAnnotationDrawing);
  const cancelAnnotationDrawing = useAppStore((state) => state.cancelAnnotationDrawing);
  const selectAnnotation = useAppStore((state) => state.selectAnnotation);
  const deselectAnnotation = useAppStore((state) => state.closeInspector);
  const moveAnnotation = useAppStore((state) => state.moveAnnotation);
  const canAuthorAnnotations = useCapability('annotations.author');
  const tableLayer = layers.find((layer) => layer.id === selectedLayerId);
  const tableSource = tableLayer
    ? sources.find((source) => source.id === tableLayer.sourceId)
    : undefined;
  const standaloneTable = sources
    .filter(isTableSource)
    .find((source) => source.id === selectedTableSourceId);
  const effectiveTableSource =
    tableLayer && tableSource && isVectorSource(tableSource)
      ? effectiveVectorSourceForLayer(sources, tableLayer)
      : null;
  const attributeTableContext =
    mode === 'composition' || !attributeTableOpen
      ? null
      : tableLayer && effectiveTableSource
        ? ({ kind: 'vector', layer: tableLayer, source: effectiveTableSource } as const)
        : standaloneTable
          ? ({ kind: 'table', source: standaloneTable } as const)
          : null;
  const effectiveMapView = canPitch ? mapView : { ...mapView, pitch: 0 };
  const effectiveBasemap = canRender3dBuildings ? basemap : { ...basemap, buildings3d: false };

  useEffect(() => {
    if (!annotationCreationTool || importInteraction) return;
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        cancelAnnotationDrawing();
      } else if (event.key === 'Enter') {
        event.preventDefault();
        finishAnnotationDrawing();
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [annotationCreationTool, cancelAnnotationDrawing, finishAnnotationDrawing, importInteraction]);

  return (
    <main
      className={`map-workspace map-workspace-${mode}${attributeTableContext ? ' map-workspace-has-table' : ''}`}
      data-map-drop-target={mode === 'editor' ? 'true' : undefined}
    >
      <div className="map-viewport">
        <CompositionCanvas
          renderMap={({ pixelsPerMm, onGroundFootprint }) => {
            const page = compositionPageDimensionsMm(compositionPage);
            const effectiveCamera = effectiveCompositionCamera(
              compositionCamera,
              compositionFrame.width * page.width,
              compositionFrame.height * page.height,
              pixelsPerMm,
            );
            const compositionView = mapViewForCompositionCamera(effectiveCamera, pixelsPerMm);
            return compositionView ? (
              <MapSurface
                importRectangle={importInteraction}
                {...(mode === 'composition' ? { onGroundFootprint } : {})}
                activeAnnotationLayerId={activeAnnotationLayerId}
                annotationDraftCoordinates={annotationDraftCoordinates}
                annotationAuthoringEnabled={
                  !importInteraction &&
                  canAuthorAnnotations &&
                  (mode === 'editor' || mode === 'composition')
                }
                annotationCreationTool={importInteraction ? null : annotationCreationTool}
                annotationLayers={annotationLayers}
                annotationRenderScale={
                  mode === 'composition' ? compositionAnnotationRenderScale(pixelsPerMm) : 1
                }
                activeFocusAreaId={activeFocusAreaId}
                basemap={effectiveBasemap}
                backgroundColour={backgroundColour}
                featureInspectionEnabled={
                  !importInteraction &&
                  mode !== 'composition' &&
                  !selectionToolActive &&
                  !annotationCreationTool
                }
                featureSelectionMethod={
                  !importInteraction && mode !== 'composition' ? selectionMethod : null
                }
                inspectedFeature={mode === 'composition' ? null : inspectedFeature}
                layers={layers}
                {...(mode === 'composition' ? { maximumZoom: 24, minimumZoom: -10 } : {})}
                onFeatureInspect={inspectFeature}
                onFeatureSelect={selectFeatureFromMap}
                onFeaturesSelect={selectFeaturesFromMap}
                onAnnotationCoordinate={(coordinate, finish) =>
                  placeAnnotationCoordinate(coordinate, finish)
                }
                onAnnotationMove={moveAnnotation}
                onAnnotationSelect={selectAnnotation}
                onAnnotationDeselect={deselectAnnotation}
                onViewChange={
                  mode === 'composition'
                    ? (view, metadata) => {
                        const camera = compositionCameraFromMapView(
                          compositionCamera,
                          view,
                          pixelsPerMm,
                          metadata.zoomChanged ? 'zoom' : 'move',
                        );
                        if (camera) updateCompositionCamera(camera);
                      }
                    : importInteraction
                      ? importInteraction.onViewChange
                      : updateMapView
                }
                selectedFeatures={mode === 'composition' ? [] : selectedFeatures}
                selectedAnnotationId={canAuthorAnnotations ? selectedAnnotationId : null}
                showAttribution={mode !== 'composition'}
                sources={sources}
                symbolAssets={symbolAssets}
                pitchEnabled={mode === 'composition' || canPitch}
                searchGeometry={mode === 'composition' ? null : (searchTarget?.geometry ?? null)}
                view={
                  mode === 'composition'
                    ? compositionView
                    : (importInteraction?.view ?? searchTarget?.view ?? effectiveMapView)
                }
              />
            ) : null;
          }}
          mode={mode}
        />
        {mode !== 'composition' ? <FeaturePopup /> : null}
        {mode !== 'composition' && !importInteraction ? <MapToolsRail key={mode} /> : null}
        {mode !== 'composition' ? (
          <>
            <SelectedFeaturesBar
              count={selectedFeatures.length}
              onClear={clearSelection}
              canCreateArea={canAuthorAnnotations}
              onCreateArea={
                mode === 'editor'
                  ? () =>
                      setAreaFeatures(
                        canonicalSelectedFeatures(sources, layers, selectedFeatures).map(
                          ({ feature }) => ({
                            type: 'Feature',
                            geometry: structuredClone(feature.geometry),
                            properties: null,
                          }),
                        ),
                      )
                  : undefined
              }
            />
            {areaFeatures ? (
              <CreateAreaDialog features={areaFeatures} onClose={() => setAreaFeatures(null)} />
            ) : null}
          </>
        ) : null}
        {!importInteraction ? (
          <MapUtilityTray
            key={`utilities-${mode}`}
            canAuthorBasemap={mode === 'editor' || mode === 'composition'}
            composition={mode === 'composition'}
          />
        ) : null}
        <div className="map-drop-indicator" aria-hidden="true">
          Drop data to add a layer
        </div>
      </div>
      {attributeTableContext ? (
        <AttributeTablePanel
          context={attributeTableContext}
          key={
            attributeTableContext.kind === 'vector'
              ? `${attributeTableContext.layer.id}:${attributeTableContext.source.id}`
              : attributeTableContext.source.id
          }
          onClose={closeAttributeTable}
        />
      ) : null}
    </main>
  );
}
